<script lang="ts">
  import { STORE } from "../../state.svelte";
  import { shortAddress } from "../../utils";
  import PoolRow from "../elements/PoolRow.svelte";
  import {
    SailfishApi,
    SailfishTier,
    PoolType,
    type RawGraduations,
    type PoolInfo,
  } from "sailfish-sdk";

  const SLOT_DURATION_MS = 400;
  const MAX_SLOT_RANGE = 10_000_000;

  const api = new SailfishApi({
    tier: SailfishTier.demo({ apiKey: STORE.bot.config.sailfish_api }),
  });

  let startDate = $state("");
  let endDate = $state("");
  let currentSlot = $state(0);
  let slotFetchTime = $state(0);
  let isLoading = $state(false);
  let currentFetchingRange: { lower_tick: number; upper_tick: number } | null = $state(null);
  let fetchProgress = $state(0);
  let fetchTotal = $state(0);
  let errorMsg = $state("");

  type Graduation = { slot: number; tx: string; poolInfo: PoolInfo };
  let graduations: Graduation[] = $state([]);

  function toDatetimeLocal(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  function dateToSlot(dateStr: string): number {
    if (!dateStr || !currentSlot || !slotFetchTime) return 0;
    const targetMs = new Date(dateStr).getTime();
    return Math.max(0, currentSlot - Math.floor((slotFetchTime - targetMs) / SLOT_DURATION_MS));
  }

  let computedStartSlot = $derived(dateToSlot(startDate));
  let computedEndSlot = $derived(dateToSlot(endDate));
  let slotRange = $derived(computedEndSlot - computedStartSlot);
  let rangeExceeded = $derived(slotRange > MAX_SLOT_RANGE);

  $effect(() => {
    (async () => {
      try {
        currentSlot = await api.fetchLatestBlock();
        slotFetchTime = Date.now();
        const now = new Date();
        endDate = toDatetimeLocal(now);
        startDate = toDatetimeLocal(new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000));
      } catch (e: any) { errorMsg = e.message ?? "Failed to fetch latest block"; }
    })();
  });

  function getChunkedRanges(start: number, end: number, chunkSize: number) {
    const ranges = [];
    for (let i = start; i < end; i += chunkSize) ranges.push({ lower_tick: i, upper_tick: Math.min(i + chunkSize, end) });
    return ranges;
  }

  const processRawGraduations = async (rawGraduations: RawGraduations) => {
    const known = new Set(graduations.map((g) => g.poolInfo.address));
    const newEntries: [string, number, string][] = [];

    for (const p of rawGraduations.pumpswap_graduations) {
      if (!known.has(p.pool)) { newEntries.push([p.pool, p.slot, p.first_signature]); known.add(p.pool); }
    }
    for (const r of rawGraduations.raydium_amm_graduations) {
      if (!known.has(r.amm_pool)) { newEntries.push([r.amm_pool, r.slot, r.first_signature]); known.add(r.amm_pool); }
    }
    for (const m of rawGraduations.raydium_cpmm_graduations) {
      if (!known.has(m.cpswap_pool)) { newEntries.push([m.cpswap_pool, m.slot, m.first_signature]); known.add(m.cpswap_pool); }
    }

    // Fetch pool info in batches of 5 to avoid overwhelming the API
    for (let i = 0; i < newEntries.length; i += 5) {
      const batch = newEntries.slice(i, i + 5);
      await Promise.all(batch.map(async ([addr, slot, tx]) => {
        try {
          let poolInfo = STORE.bot.sailfish.getPoolInfo(addr);
          if (!poolInfo) {
            const result = await STORE.bot.sailfish.fetchPoolInfo(addr);
            if (result instanceof Error) return;
            poolInfo = result;
          }
          graduations = [...graduations, { slot, tx, poolInfo }];
        } catch {}
      }));
    }
  };

  async function fetchGraduations() {
    if (isLoading) return;
    errorMsg = "";
    if (rangeExceeded) { errorMsg = `Range exceeds max (~${Math.round((MAX_SLOT_RANGE * SLOT_DURATION_MS) / 60_000)} min).`; return; }
    if (computedStartSlot >= computedEndSlot) { errorMsg = "Start must be before end."; return; }

    isLoading = true;
    graduations = [];
    // Use 10k chunk size to avoid 500 errors on large ranges
    const ranges = getChunkedRanges(computedStartSlot, computedEndSlot, 1_000);
    fetchTotal = ranges.length;
    fetchProgress = 0;
    for (const range of ranges) {
      currentFetchingRange = range;
      fetchProgress++;
      try {
        const raw = await api.fetchRawGraduations({
          lower_tick: range.lower_tick,
          upper_tick: range.upper_tick,
          pool_types: [PoolType.PumpFunAmm, PoolType.RaydiumLaunchpad],
          pool_addresses: [],
          token_addresses: [],
        });
        await processRawGraduations(raw);
      } catch (e: any) {
        errorMsg = e.message ?? "Failed to fetch graduations";
        isLoading = false;
        return;
      }
      await new Promise((r) => setTimeout(r, 300));
    }
    isLoading = false;
    currentFetchingRange = null;
  }

  function downloadGraduationsCsv() {
    const header = "slot,pool_type,base_token,quote_token,pool_address,tx_hash";
    const rows = graduations.map((g) =>
      [g.slot, g.poolInfo.pool_type, g.poolInfo.base_token.symbol, g.poolInfo.quote_token.symbol, g.poolInfo.address, g.tx].join(",")
    );
    const blob = new Blob([header + "\n" + rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `graduations_${graduations.length}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }
</script>

<div class="px-4">
  <!-- Query Form -->
  <div class="max-w-2xl mx-auto mb-8">
    <div class="bg-gray-950/40 border border-gray-800 rounded-sm p-5">
      <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-4">Query Parameters</p>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label class="text-[10px] font-mono text-gray-500 uppercase tracking-widest block mb-1">Start</label>
          <input type="datetime-local" class="input input-sm bg-black border-gray-800 text-white font-mono text-sm w-full rounded-sm" bind:value={startDate} />
        </div>
        <div>
          <label class="text-[10px] font-mono text-gray-500 uppercase tracking-widest block mb-1">End</label>
          <input type="datetime-local" class="input input-sm bg-black border-gray-800 text-white font-mono text-sm w-full rounded-sm" bind:value={endDate} />
        </div>
      </div>
      {#if computedStartSlot > 0 && computedEndSlot > 0}
        <div class="flex flex-wrap items-center gap-2 mb-4">
          <span class="badge badge-outline rounded-none font-mono text-[10px] {rangeExceeded ? 'text-red-500 border-red-500/50' : 'text-gray-500 border-gray-700'} p-2">
            ~{Math.round((slotRange * SLOT_DURATION_MS) / 60_000)} min
          </span>
        </div>
      {/if}
      {#if errorMsg}<div class="text-red-500 text-xs font-mono mb-3">{errorMsg}</div>{/if}
      <button class="btn bg-sky-600 hover:bg-sky-500 text-white border-none rounded-sm uppercase tracking-widest font-bold text-xs transition-colors w-full" onclick={fetchGraduations} disabled={isLoading}>
        {isLoading ? "Fetching..." : "Fetch Graduations"}
      </button>
    </div>
  </div>

  {#if isLoading && currentFetchingRange}
    <div class="mb-6">
      <div class="flex items-center justify-between mb-2">
        <span class="text-[10px] font-mono text-sky-500 uppercase tracking-widest">
          Fetching batch {fetchProgress} / {fetchTotal}
        </span>
        <div class="flex items-center gap-3">
          {#if graduations.length > 0}
            <button onclick={downloadGraduationsCsv} class="btn btn-xs rounded-sm bg-sky-600/10 border border-sky-600/20 text-sky-500 hover:bg-sky-600/20 font-mono text-[10px] uppercase tracking-widest transition-colors">
              Download CSV ({graduations.length})
            </button>
          {/if}
          <span class="text-[10px] font-mono text-gray-600 tabular-nums">
            {((computedEndSlot - currentFetchingRange.upper_tick)).toLocaleString()} slots remaining
          </span>
        </div>
      </div>
      <div class="h-1 bg-gray-800 rounded-full overflow-hidden">
        <div class="h-full bg-sky-600 transition-all duration-300" style="width: {fetchTotal > 0 ? (fetchProgress / fetchTotal * 100) : 0}%"></div>
      </div>
    </div>
  {/if}

  {#if graduations.length > 0}
    <div class="overflow-x-auto rounded-sm border border-gray-800 bg-gray-950/40 p-4">
      <div class="flex items-center gap-3 mb-3">
        <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest">Graduations <span class="text-gray-700">({graduations.length})</span></p>
        <button onclick={downloadGraduationsCsv} class="btn btn-xs rounded-sm bg-sky-600/10 border border-sky-600/20 text-sky-500 hover:bg-sky-600/20 font-mono text-[10px] uppercase tracking-widest transition-colors">
          Download CSV
        </button>
      </div>
      <table class="table table-xs">
        <thead><tr><th>Slot</th><th>Pool Type</th><th>Pair</th><th>Tx</th><th>Address</th></tr></thead>
        <tbody>
          {#each graduations as graduation}
            <PoolRow
              rank={graduation.slot}
              poolType={graduation.poolInfo.pool_type}
              poolAddress={graduation.poolInfo.address}
              pair={`${graduation.poolInfo.base_token.symbol}/${graduation.poolInfo.quote_token.symbol}`}
              trades="-"
              wallets={shortAddress(graduation.tx, 8, 8)}
              getPoolInfo={undefined}
            />
          {/each}
        </tbody>
      </table>
    </div>
  {:else if !isLoading && currentSlot > 0}
    <div class="text-center py-12"><p class="text-gray-600 text-sm">Set a date range, then click Fetch to load pool graduations.</p></div>
  {/if}
</div>
